import { useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Loader2, LogOut, Users, DollarSign, ListChecks, Activity, ClipboardList, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { adminFetchLeads, adminFetchStats, adminFetchPayments } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import ProjectsTab from "@/components/admin/ProjectsTab";
import LeadDetailDialog from "@/components/admin/LeadDetailDialog";
import ChangePasswordDialog from "@/components/admin/ChangePasswordDialog";

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="bento-card p-6" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs font-mono-jb uppercase tracking-widest text-zinc-500">
            {label}
          </div>
          <div className="mt-2 font-display text-4xl font-black">{value}</div>
        </div>
        <div className="w-12 h-12 rounded-xl bg-[#CCFF00]/10 border border-[#CCFF00]/30 flex items-center justify-center">
          <Icon className="w-5 h-5 text-[#CCFF00]" />
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const [password, setPassword] = useState("");
  const [auth, setAuth] = useState(false);
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState({ total_leads: 0, new_leads: 0, total_paid: 0, revenue: 0 });
  const [activeLead, setActiveLead] = useState(null);
  const [pwOpen, setPwOpen] = useState(false);

  const login = async (e) => {
    e?.preventDefault?.();
    if (!password) return;
    try {
      setLoading(true);
      const [s, l, p] = await Promise.all([
        adminFetchStats(password),
        adminFetchLeads(password),
        adminFetchPayments(password),
      ]);
      setStats(s);
      setLeads(l);
      setPayments(p);
      setAuth(true);
      toast.success("Welcome back, admin.");
    } catch (e) {
      toast.error("Wrong password.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setAuth(false);
    setPassword("");
    setLeads([]);
    setPayments([]);
  };

  if (!auth) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="w-full max-w-md bg-[#111] border border-[#27272A] rounded-3xl p-10">
          <div className="w-12 h-12 mx-auto rounded-xl bg-[#CCFF00]/10 border border-[#CCFF00]/30 flex items-center justify-center">
            <Lock className="w-5 h-5 text-[#CCFF00]" />
          </div>
          <h1 className="font-display text-3xl font-black tracking-tight text-center mt-6">
            Admin <span className="text-gradient-neon">Console</span>
          </h1>
          <p className="text-zinc-400 text-center mt-2">
            Enter the admin password to continue.
          </p>
          <form onSubmit={login} className="mt-8 space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-400 font-mono-jb">
                Password
              </Label>
              <Input
                data-testid={ADMIN.passwordInput}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 bg-[#0a0a0a] border-[#27272A] focus-visible:ring-[#CCFF00] focus-visible:border-[#CCFF00]"
                placeholder="••••••••"
                required
              />
            </div>
            <Button
              data-testid={ADMIN.loginBtn}
              disabled={loading}
              type="submit"
              className="btn-neon rounded-none w-full h-12"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Sign in"
              )}
            </Button>
            <Link
              to="/"
              className="block text-center text-zinc-500 hover:text-[#CCFF00] text-sm font-mono-jb mt-4"
            >
              ← Back to site
            </Link>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-6 md:px-12 py-10 max-w-7xl mx-auto" data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-10">
        <div>
          <span className="font-mono-jb text-xs uppercase tracking-[0.3em] text-[#CCFF00]">
            / Admin
          </span>
          <h1 className="font-display text-4xl font-black tracking-tighter mt-2">
            Lead & Payment Dashboard
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="admin-change-pw-btn"
            onClick={() => setPwOpen(true)}
            variant="outline"
            className="btn-outline-neon rounded-none"
          >
            <KeyRound className="w-4 h-4 mr-2" /> Change Password
          </Button>
          <Button
            data-testid={ADMIN.logoutBtn}
            onClick={logout}
            variant="outline"
            className="btn-outline-neon rounded-none"
          >
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <Stat
          icon={Users}
          label="Total Leads"
          value={stats.total_leads}
          testid={ADMIN.statsTotalLeads}
        />
        <Stat icon={ListChecks} label="New Leads" value={stats.new_leads} testid="admin-stat-new-leads" />
        <Stat icon={Activity} label="Paid Orders" value={stats.total_paid} testid="admin-stat-paid" />
        <Stat
          icon={DollarSign}
          label="Revenue"
          value={`$${stats.revenue || 0}`}
          testid={ADMIN.statsRevenue}
        />
      </div>

      <Tabs defaultValue="projects">
        <TabsList className="bg-[#111] border border-[#27272A] rounded-full">
          <TabsTrigger
            value="projects"
            data-testid="admin-tab-projects"
            className="data-[state=active]:bg-[#CCFF00] data-[state=active]:text-black rounded-full px-6"
          >
            <ClipboardList className="w-4 h-4 mr-2" /> Projects
          </TabsTrigger>
          <TabsTrigger
            value="leads"
            data-testid="admin-tab-leads"
            className="data-[state=active]:bg-[#CCFF00] data-[state=active]:text-black rounded-full px-6"
          >
            Leads ({leads.length})
          </TabsTrigger>
          <TabsTrigger
            value="payments"
            data-testid="admin-tab-payments"
            className="data-[state=active]:bg-[#CCFF00] data-[state=active]:text-black rounded-full px-6"
          >
            Payments ({payments.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="projects" className="mt-6">
          <ProjectsTab password={password} />
        </TabsContent>

        <TabsContent value="leads" className="mt-6">
          <div className="bento-card overflow-hidden" data-testid={ADMIN.leadsTable}>
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-white/10">
                  <TableHead className="text-zinc-400">Name</TableHead>
                  <TableHead className="text-zinc-400">Email</TableHead>
                  <TableHead className="text-zinc-400">Phone</TableHead>
                  <TableHead className="text-zinc-400">Business</TableHead>
                  <TableHead className="text-zinc-400">Logo</TableHead>
                  <TableHead className="text-zinc-400">Package</TableHead>
                  <TableHead className="text-zinc-400">Reference</TableHead>
                  <TableHead className="text-zinc-400">Status</TableHead>
                  <TableHead className="text-zinc-400">Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-zinc-500 py-12">
                      No leads yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  leads.map((l) => (
                    <TableRow
                      key={l.id}
                      data-testid={`admin-lead-row-${l.id}`}
                      onClick={() => setActiveLead(l)}
                      className="border-white/5 hover:bg-white/5 cursor-pointer"
                    >
                      <TableCell className="font-medium">{l.name}</TableCell>
                      <TableCell>{l.email}</TableCell>
                      <TableCell>{l.phone || "—"}</TableCell>
                      <TableCell>{l.business_name || "—"}</TableCell>
                      <TableCell>
                        {l.logo_data_url ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveLead(l);
                            }}
                            title="View details"
                            data-testid={`admin-lead-logo-${l.id}`}
                          >
                            <img
                              src={l.logo_data_url}
                              alt="logo"
                              className="w-10 h-10 object-contain bg-black/30 rounded border border-white/10 hover:border-[#CCFF00]"
                            />
                          </button>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell>{l.interested_package || "—"}</TableCell>
                      <TableCell>
                        {l.reference_website ? (
                          <a
                            onClick={(e) => e.stopPropagation()}
                            href={l.reference_website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#CCFF00] hover:underline text-xs font-mono-jb max-w-[140px] inline-block truncate align-middle"
                            title={l.reference_website}
                          >
                            {l.reference_website.replace(/^https?:\/\//, "")}
                          </a>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="px-2 py-1 rounded-full bg-[#CCFF00]/10 text-[#CCFF00] text-xs font-mono-jb">
                          {l.status}
                        </span>
                      </TableCell>
                      <TableCell className="text-zinc-500 text-xs font-mono-jb">
                        {new Date(l.created_at).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="payments" className="mt-6">
          <div className="bento-card overflow-hidden" data-testid={ADMIN.paymentsTable}>
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-white/10">
                  <TableHead className="text-zinc-400">Package</TableHead>
                  <TableHead className="text-zinc-400">Amount</TableHead>
                  <TableHead className="text-zinc-400">Status</TableHead>
                  <TableHead className="text-zinc-400">Email</TableHead>
                  <TableHead className="text-zinc-400">Session</TableHead>
                  <TableHead className="text-zinc-400">Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-zinc-500 py-12">
                      No transactions yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  payments.map((p) => (
                    <TableRow key={p.session_id} className="border-white/5 hover:bg-white/5">
                      <TableCell className="font-medium">{p.package_name}</TableCell>
                      <TableCell>${p.amount}</TableCell>
                      <TableCell>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-mono-jb ${
                            p.payment_status === "paid"
                              ? "bg-[#CCFF00]/10 text-[#CCFF00]"
                              : "bg-zinc-500/10 text-zinc-400"
                          }`}
                        >
                          {p.payment_status}
                        </span>
                      </TableCell>
                      <TableCell>{p.email || "—"}</TableCell>
                      <TableCell className="font-mono-jb text-xs text-zinc-500">
                        {p.session_id?.slice(0, 16)}…
                      </TableCell>
                      <TableCell className="text-zinc-500 text-xs font-mono-jb">
                        {new Date(p.created_at).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <LeadDetailDialog lead={activeLead} onClose={() => setActiveLead(null)} />
      <ChangePasswordDialog
        open={pwOpen}
        onOpenChange={setPwOpen}
        currentPassword={password}
        onPasswordChanged={() => logout()}
      />
    </div>
  );
}
